import { useEffect, useState } from 'react';

const BREAKPOINTS = {
  sm: '(min-width: 640px)',
  md: '(min-width: 768px)',
  lg: '(min-width: 1024px)',
  xl: '(min-width: 1280px)',
  '2xl': '(min-width: 1536px)',
} as const;

type Breakpoint = keyof typeof BREAKPOINTS;

export type ResponsiveState = Record<Breakpoint, boolean>;

function getInitialState(): ResponsiveState {
  if (typeof window === 'undefined') {
    return { sm: false, md: false, lg: false, xl: false, '2xl': false };
  }

  return (Object.entries(BREAKPOINTS) as [Breakpoint, string][]).reduce(
    (acc, [key, query]) => ({
      ...acc,
      [key]: window.matchMedia(query).matches,
    }),
    {} as ResponsiveState
  );
}

export function useResponsive(): ResponsiveState {
  const [state, setState] = useState<ResponsiveState>(getInitialState);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const mediaQueries = (
      Object.entries(BREAKPOINTS) as [Breakpoint, string][]
    ).map(([key, query]) => {
      const media = window.matchMedia(query);

      const listener = (event: MediaQueryListEvent) => {
        setState(prev => ({ ...prev, [key]: event.matches }));
      };

      media.addEventListener('change', listener);

      return { media, listener };
    });

    return () => {
      mediaQueries.forEach(({ media, listener }) => {
        media.removeEventListener('change', listener);
      });
    };
  }, []);

  return state;
}
